/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react/prop-types, no-console */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Space, message, Spin, Badge } from 'antd';
import { LeftOutlined, RightOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const DocumentPreviewModal = ({ open, onClose, documents, onViewDocument }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [documentUrls, setDocumentUrls] = useState({});
	const [loadingDocs, setLoadingDocs] = useState({});

	// Reset when modal opens/closes or documents change
	useEffect(() => {
		if (open && documents?.length > 0) {
			setCurrentIndex(0);
			setDocumentUrls({});
			setLoadingDocs({});
		}
	}, [open, documents]);

	// Load document URL when needed
	const loadDocumentUrl = useCallback(
		async (doc) => {
			const docId = doc?.fileId || doc?.id;
			if (!docId || documentUrls[docId] || loadingDocs[docId]) return;

			// Check if the document has a valid ID
			if (!docId) {
				console.warn('Document missing fileId/id:', doc);
				return;
			}

			setLoadingDocs((prev) => ({ ...prev, [docId]: true }));

			try {
				// Call the onViewDocument function to get the document
				if (onViewDocument) {
					console.log('Loading document:', docId, doc.fileName || doc.originalFileName);
					// Assuming onViewDocument returns a URL or blob URL
					const url = await onViewDocument(
						docId,
						doc.fileName || doc.originalFileName,
						true,
					); // Add preview flag
					if (url) {
						setDocumentUrls((prev) => ({ ...prev, [docId]: url }));
					} else {
						console.warn('No URL returned for document:', docId);
					}
				}
			} catch (error) {
				console.error('Error loading document:', docId, error);
				// Only show error message if it's not a "not found" type error
				if (!error.message?.includes('404') && !error.message?.includes('not found')) {
					message.error(
						`Failed to load document: ${doc.fileName || doc.originalFileName}`,
					);
				}
			} finally {
				setLoadingDocs((prev) => ({ ...prev, [docId]: false }));
			}
		},
		[documentUrls, loadingDocs, onViewDocument],
	);

	// Load current document when index changes
	useEffect(() => {
		if (documents?.length > 0 && currentIndex >= 0 && currentIndex < documents.length) {
			const currentDoc = documents[currentIndex];
			loadDocumentUrl(currentDoc);
		}
	}, [currentIndex, documents, loadDocumentUrl]);

	const handlePrevious = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	};

	const handleNext = () => {
		if (currentIndex < documents.length - 1) {
			setCurrentIndex(currentIndex + 1);
		}
	};

	const handleDownload = () => {
		const currentDoc = documents[currentIndex];
		if (currentDoc && onViewDocument) {
			const docId = currentDoc.fileId || currentDoc.id;
			onViewDocument(docId, currentDoc.fileName || currentDoc.originalFileName, false); // Download flag
		}
	};

	const getFileExtension = (fileName) => {
		return fileName?.split('.').pop()?.toLowerCase() || '';
	};

	const isImage = (fileName) => {
		const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
		return imageExtensions.includes(getFileExtension(fileName));
	};

	const isPdf = (fileName) => {
		return getFileExtension(fileName) === 'pdf';
	};

	const renderDocumentPreview = () => {
		if (!documents?.length) return null;

		const currentDoc = documents[currentIndex];
		const docId = currentDoc.fileId || currentDoc.id;
		const fileName = currentDoc.fileName || currentDoc.originalFileName;
		const isLoading = loadingDocs[docId];
		const url = documentUrls[docId];

		if (isLoading) {
			return (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '500px',
					}}>
					<Spin size='large' />
				</div>
			);
		}

		if (!url) {
			return (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '500px',
						color: '#666',
					}}>
					Document not available
				</div>
			);
		}

		if (isImage(fileName)) {
			return (
				<div style={{ textAlign: 'center' }}>
					<img
						src={url}
						alt={fileName}
						style={{
							maxWidth: '100%',
							maxHeight: '500px',
							objectFit: 'contain',
						}}
						onError={() => {
							message.error('Failed to load image');
						}}
					/>
				</div>
			);
		}

		if (isPdf(fileName)) {
			return (
				<div style={{ textAlign: 'center' }}>
					<iframe
						src={url}
						title={fileName}
						style={{
							width: '100%',
							height: '500px',
							border: 'none',
						}}
						onError={() => {
							message.error('Failed to load PDF');
						}}
					/>
				</div>
			);
		}

		// For other file types, show a placeholder
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '500px',
					flexDirection: 'column',
					color: '#666',
				}}>
				<div style={{ fontSize: '16px', marginBottom: '10px' }}>
					Preview not available for this file type
				</div>
				<div style={{ fontSize: '14px' }}>File: {fileName}</div>
				<Button
					type='primary'
					icon={<DownloadOutlined />}
					onClick={handleDownload}
					style={{ marginTop: '20px' }}>
					Download File
				</Button>
			</div>
		);
	};

	if (!documents?.length) return null;

	const currentDoc = documents[currentIndex];
	const expiryDate = currentDoc?.expiryDate;
	const formattedExpiry = expiryDate ? dayjs(expiryDate).format('DD/MM/YYYY') : null;
	const isExpired = expiryDate ? dayjs(expiryDate).isBefore(dayjs(), 'day') : false;

	return (
		<Modal
			title={currentDoc?.fileName || currentDoc?.originalFileName || 'Document Preview'}
			open={open}
			onCancel={onClose}
			width={800}
			footer={
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}>
					<Space>
						<Button
							icon={<LeftOutlined />}
							onClick={handlePrevious}
							disabled={currentIndex === 0}>
							Previous
						</Button>
						<Button
							icon={<RightOutlined />}
							onClick={handleNext}
							disabled={currentIndex === documents.length - 1}>
							Next
						</Button>
					</Space>
					<div
						style={{
							fontSize: '14px',
							fontWeight: 'normal',
							color: '#666',
						}}>
						{currentIndex + 1} of {documents.length}
					</div>
					<Space>
						<Button icon={<DownloadOutlined />} onClick={handleDownload}>
							Download
						</Button>
						<Button icon={<CloseOutlined />} onClick={onClose}>
							Close
						</Button>
					</Space>
				</div>
			}>
			{/* Expiry Date Badge */}
			{formattedExpiry && (
				<div style={{ 
					marginBottom: 16, 
					padding: '12px 16px', 
					backgroundColor: isExpired ? '#fff2f0' : '#f6ffed',
					border: `1px solid ${isExpired ? '#ffccc7' : '#b7eb8f'}`,
					borderRadius: 6,
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center'
				}}>
					<span style={{ fontWeight: 'bold' }}>Expiry Date:</span>
					<Badge 
						status={isExpired ? 'error' : 'success'} 
						text={
							<span style={{ 
								fontWeight: 'bold',
								color: isExpired ? '#ff4d4f' : '#52c41a'
							}}>
								{formattedExpiry} {isExpired && '(EXPIRED)'}
							</span>
						}
					/>
				</div>
			)}
			{renderDocumentPreview()}
		</Modal>
	);
};

export default DocumentPreviewModal;
/* eslint-enable react/prop-types, no-console */
